// A side card that folds to its header (S136): icon, title and, while folded, a one-line summary
// the card provides (chips and micro charts that drop out as room runs short; see
// ui/summary.tsx). The summary sits beside the title when the header is wide (26rem and up) and
// on its own line under the title otherwise, so it never squeezes the title or overflows. The
// whole header toggles the card; the chevron is the button (aria-expanded), and the folded body
// is inert. The choice is remembered per browser per kind of card, unless `remember` is false
// (S144: the task page's Steps card always starts folded).
import { CaretDown } from "@phosphor-icons/react";
import { type ReactNode, useId } from "react";

import { useT } from "../i18n";
import { cardClass } from "./Card";
import { useCardOpen } from "./cardOpen";
import { cn } from "./cn";

export function CollapsibleCard({
  kind,
  icon,
  title,
  description,
  action,
  summary,
  className,
  remember = true,
  defaultOpen = true,
  children,
}: {
  /** Names the remembered choice: one per kind of card. */
  kind: string;
  icon: ReactNode;
  title: string;
  description?: ReactNode;
  /** Shown beside the title while the card is open. */
  action?: ReactNode;
  /** Shown in the header while the card is folded; keep it to one line of chips. */
  summary?: ReactNode;
  className?: string;
  /** False: start as `defaultOpen` on every visit and remember nothing. */
  remember?: boolean;
  defaultOpen?: boolean;
  children?: ReactNode;
}) {
  const t = useT().common.card;
  const [open, setOpen] = useCardOpen(kind, { remember, defaultOpen });
  const bodyId = useId();
  return (
    <section className={cn(cardClass, open ? "p-5 lg:p-6" : "px-5 py-4 lg:px-6", className)} aria-label={title} data-card={kind} data-open={open}>
      <div
        className={cn(
          "@container/card relative flex min-w-0 flex-wrap gap-x-3 gap-y-1.5 @[26rem]/card:flex-nowrap",
          open ? "items-start" : "items-center",
        )}
      >
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-lighter text-primary [&>svg]:h-[18px] [&>svg]:w-[18px]"
          aria-hidden
        >
          {icon}
        </span>
        <div className={cn("min-w-0 flex-1", !open && "@[26rem]/card:flex-initial")}>
          <h3 className="truncate text-base font-semibold text-gray-900">{title}</h3>
          {open && description ? <div className="mt-0.5 text-[13px] text-gray-500">{description}</div> : null}
        </div>
        {!open && summary ? (
          <div className="@container order-last min-w-0 basis-full ps-11 @[26rem]/card:order-none @[26rem]/card:basis-0 @[26rem]/card:flex-1 @[26rem]/card:ps-0" data-testid={`${kind}-summary`}>
            {summary}
          </div>
        ) : null}
        {open && action ? (
          // Under the title on a narrow card (it would squeeze the title), beside it on a wide one.
          <div className="relative z-10 order-last basis-full ps-11 @[26rem]/card:order-none @[26rem]/card:basis-auto @[26rem]/card:shrink-0 @[26rem]/card:ps-0">
            {action}
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={open ? t.collapse(title) : t.expand(title)}
          title={open ? t.collapse(title) : t.expand(title)}
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            // The whole header row is the hit area (the action above it stays clickable).
            "after:absolute after:inset-0 after:cursor-pointer after:content-['']",
          )}
        >
          <CaretDown weight="bold" className={cn("h-4 w-4 transition-transform motion-reduce:transition-none", open && "rotate-180")} aria-hidden />
        </button>
      </div>
      <div
        id={bodyId}
        className={cn(
          "grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
        inert={!open}
      >
        {/* The negative margins leave room for focus rings at the edges of the clipped body. */}
        <div className="-mx-1 min-h-0 overflow-hidden px-1">
          <div className="pb-1 pt-4">{children}</div>
        </div>
      </div>
    </section>
  );
}
